import type { apm } from '@elastic/apm-rum'
import { createSharedComposable, tryOnScopeDispose } from '@vueuse/core'

export type ApmUser = { id: string | number }
export type ApmUserProvider = () => ApmUser | null | undefined

interface UseApmUserReturn {
    registerUser: (provider: ApmUserProvider) => void
    applyUser: (agent: Pick<typeof apm, 'setUserContext'> | undefined) => void
}

/**
 * APM user-context registry. Features register a provider that returns the logged-in
 * identity (or null); the APM plugin applies the merged result to the agent via
 * `setUserContext`, once on agent-ready and again whenever a provider's source changes.
 *
 * Unlike `useApmLabels` (per-transaction `labels.*`), this writes the agent's GLOBAL user
 * context, which Elastic indexes as ECS `user.id` and attaches to both transactions and
 * errors — matching how the backend keys its logs.
 *
 * The agent has no API to clear user context (it merges and never deletes keys), so
 * `applyUser` always writes: `{ id: '' }` when no provider contributes, actively blanking a
 * prior id rather than leaving it stale on a logged-out session.
 */
export const useApmUser = createSharedComposable((): UseApmUserReturn => {
    const providers = new Set<ApmUserProvider>()

    const registerUser = (provider: ApmUserProvider): void => {
        providers.add(provider)
        tryOnScopeDispose(() => {
            providers.delete(provider)
        })
    }

    const applyUser = (agent: Pick<typeof apm, 'setUserContext'> | undefined): void => {
        if (typeof agent?.setUserContext !== 'function') return
        // Resolve providers (last-wins; normally only one). Read only `id` so no other
        // field a provider might return can ever reach RUM. A throwing provider is
        // contained here because applyUser runs outside the agent's observe() wrapper.
        let id: string | undefined
        for (const provide of providers) {
            let user: ApmUser | null | undefined
            try {
                user = provide()
            }
            catch {
                continue
            }
            if (user?.id != null) id = String(user.id)
        }
        agent.setUserContext({ id: id ?? '' })
    }

    return { registerUser, applyUser }
})
